/**
 * 台灣股票交易小數點處理工具
 * 根據台灣證券交易所和櫃買中心的規則實現
 */

export class DecimalHelper {
  /**
   * 成交金額計算:無條件捨去到整數元(台股慣例)
   * @param quantity 股數
   * @param price 成交價
   * @returns 成交金額 (整數)
   */
  static calculateTradeAmount(quantity: number, price: number): number {
    if (!Number.isFinite(quantity) || !Number.isFinite(price)) {
      return 0;
    }

    const result = quantity * price;
    // 先將結果限制在 4 位小數內以避免浮點誤差，最後再無條件捨去到整數元
    const normalizedResult = Number((result + Number.EPSILON).toFixed(4));
    return Math.floor(normalizedResult);
  }

  /**
   * 手續費計算：無條件捨去到整數元
   * 注意:部分券商有「未滿 20 元按 20 元計收」的最低收費,本系統預設不啟用
   * (使用者券商無最低收費;如有需要可透過 minFee 參數或手動覆寫 fees 調整)
   * @param tradeAmount 成交金額
   * @param rate 手續費率 (預設 0.1425%)
   * @param minFee 單筆最低手續費 (預設 0 = 無最低收費)
   * @returns 手續費 (整數)
   */
  static calculateBrokerageFee(tradeAmount: number, rate: number = 0.001425, minFee: number = 0): number {
    return Math.max(minFee, Math.floor(tradeAmount * rate));
  }

  /**
   * 交易稅計算：小數點以下無條件捨去到整數元,未達 minTax 元無條件進位(預設最低 1 元)
   * 券商代徵實務(如富果官方文件):「手續費、證交稅如果有小數點以下,無條件捨去
   * (若未達 1 元,則無條件進位,最低 1 元)」
   * 注意:財政部 10504524600 號令之「元以下四捨五入」僅適用雙幣ETF外幣部位,一般股票不適用
   * @param tradeAmount 成交金額
   * @param rate 交易稅率 (股票: 0.3%, ETF: 0.1%, 當沖股票: 0.15%)
   * @param minTax 單筆最低交易稅 (預設 1 元,券商代徵實務;設 0 可停用)
   * @returns 交易稅 (整數)
   */
  static calculateTransactionTax(tradeAmount: number, rate: number = 0.003, minTax: number = 1): number {
    if (tradeAmount <= 0 || rate <= 0) {
      return 0; // 稅率 0(如債券ETF停徵)不課稅,也不觸發最低 1 元收費
    }
    return Math.max(minTax, Math.floor(tradeAmount * rate));
  }

  /** 債券ETF證交稅停徵期間起點(106/1/1,證券交易稅條例第2-1條第2項) */
  static readonly BOND_ETF_TAX_EXEMPTION_START = new Date('2017-01-01T00:00:00+08:00');
  /** 債券ETF證交稅停徵期間迄點(115/12/31,即 2026-12-31;屆期未再延續則恢復 0.1%) */
  static readonly BOND_ETF_TAX_EXEMPTION_END = new Date('2027-01-01T00:00:00+08:00');

  /**
   * 依證券代號(及交易日期)判別證交稅率(賣出時適用)
   * 稅率為法定(證券交易稅條例第2條),全市場一致,與券商無關:
   *  - 股票(含特別股如 2887I):0.3%(第2條第1款)
   *  - ETF受益憑證等:0.1%(第2條第2款;ETF 代號以 "00" 開頭,含主動式/槓桿型)
   *  - 債券ETF(代號 B 結尾,如 00679B):106/1/1~115/12/31 停徵 0%(第2-1條第2項,
   *    槓桿型及反向型不適用——其代號結尾為 L/R,不會命中 B 結尾規則)
   * 注意:現股當沖 0.15% 優惠稅率(第2-2條)系統尚不支援,如有當沖交易請手動覆寫稅額
   * @param symbol 證券代號
   * @param asOf 交易日期(判斷是否於債券ETF停徵期間;預設今天)
   * @returns 稅率 (債券ETF停徵: 0, 其他ETF: 0.001, 股票: 0.003)
   */
  static getTransactionTaxRate(symbol: string, asOf?: Date): number {
    const normalized = (symbol || '').trim().toUpperCase();
    const at = asOf instanceof Date && !Number.isNaN(asOf.getTime()) ? asOf : new Date();

    // 債券ETF:B 結尾;僅於停徵期間內為 0,屆期自動恢復 0.1%
    if (/^00\d{2,4}B$/.test(normalized)) {
      const exempt = at >= DecimalHelper.BOND_ETF_TAX_EXEMPTION_START
        && at < DecimalHelper.BOND_ETF_TAX_EXEMPTION_END;
      return exempt ? 0 : 0.001;
    }
    return /^00\d{2,4}[A-Z]?$/.test(normalized) ? 0.001 : 0.003;
  }

  /**
   * 現金股利計算:無條件捨去到整數元(實際撥付以元為單位)
   * 捨入規則與 calculateTradeAmount 一致,確保存入的 total 與含息損益重算一致
   * @param shareCount 持股股數
   * @param dividendPerShare 每股股利(公告值,可達小數 8 位)
   * @returns 股利金額 (整數元)
   */
  static calculateCashDividend(shareCount: number, dividendPerShare: number): number {
    // 與 calculateTradeAmount 相同的捨入規則(無條件捨去到整數元),
    // 確保含息損益重算的金額與交易當下儲存的 total 一致,不因進位產生兩套帳
    const result = shareCount * dividendPerShare;
    const normalizedResult = Number((result + Number.EPSILON).toFixed(4));
    return Math.floor(normalizedResult);
  }

  /**
   * 股票股利計算：只發整股，小數部分捨去
   * @param shareCount 持股股數
   * @param stockDividendRatio 股票股利比率 (如 0.1 表示每10股配1股)
   * @returns 股票股利股數 (整數)
   */
  static calculateStockDividend(shareCount: number, stockDividendRatio: number): number {
    return Math.floor(shareCount * stockDividendRatio);
  }

  /**
   * 二代健保費計算：以元為單位,角以下四捨五入
   * 依健保署規定:
   *  - 股利單次給付達 20,000 元(含)就源扣取,以全額計算
   *  - 單次給付以 1,000 萬元為限(計費基礎上限)
   *  - 保費計算「以元為單位,角以下 4 捨 5 入」(健保署官方 Q&A)
   * @param dividendAmount 股利金額
   * @param threshold 起徵點 (預設 20,000 元)
   * @param rate 費率 (預設 2.11%)
   * @returns 二代健保費 (整數)
   */
  static calculateSupplementaryPremium(
    dividendAmount: number, 
    threshold: number = 20000, 
    rate: number = 0.0211
  ): number {
    if (dividendAmount < threshold) {
      return 0;
    }
    // 計費基礎以 1,000 萬元為限
    const chargeableBase = Math.min(dividendAmount, 10_000_000);
    return Math.round(chargeableBase * rate);
  }

  /**
   * 買入總成本計算
   * @param quantity 股數
   * @param price 成交價
   * @param brokerageRate 手續費率
   * @returns { tradeAmount: number, brokerageFee: number, totalCost: number }
   */
  static calculateBuyCost(quantity: number, price: number, brokerageRate: number = 0.001425) {
    const tradeAmount = this.calculateTradeAmount(quantity, price);
    const brokerageFee = this.calculateBrokerageFee(tradeAmount, brokerageRate);
    const totalCost = tradeAmount + brokerageFee;
    
    return {
      tradeAmount,
      brokerageFee,
      totalCost
    };
  }

  /**
   * 賣出淨收入計算
   * @param quantity 股數
   * @param price 成交價
   * @param brokerageRate 手續費率
   * @param taxRate 交易稅率
   * @returns { tradeAmount: number, brokerageFee: number, transactionTax: number, netIncome: number }
   */
  static calculateSellIncome(
    quantity: number, 
    price: number, 
    brokerageRate: number = 0.001425,
    taxRate: number = 0.003
  ) {
    const tradeAmount = this.calculateTradeAmount(quantity, price);
    const brokerageFee = this.calculateBrokerageFee(tradeAmount, brokerageRate);
    const transactionTax = this.calculateTransactionTax(tradeAmount, taxRate);
    const netIncome = tradeAmount - brokerageFee - transactionTax;
    
    return {
      tradeAmount,
      brokerageFee,
      transactionTax,
      netIncome
    };
  }

  /**
   * 股利收入計算 (含二代健保費)
   * @param shareCount 持股股數
   * @param dividendPerShare 每股股利
   * @param brokerageFee 股利匯費 (預設 10 元)
   * @returns { dividendAmount: number, supplementaryPremium: number, brokerageFee: number, netDividend: number }
   */
  static calculateDividendIncome(
    shareCount: number, 
    dividendPerShare: number, 
    brokerageFee: number = 10
  ) {
    const dividendAmount = this.calculateCashDividend(shareCount, dividendPerShare);
    const supplementaryPremium = this.calculateSupplementaryPremium(dividendAmount);
    const netDividend = dividendAmount - supplementaryPremium - brokerageFee;
    
    return {
      dividendAmount,
      supplementaryPremium,
      brokerageFee,
      netDividend
    };
  }

  /**
   * 驗證計算結果
   * @param actualAmount 實際金額
   * @param expectedAmount 預期金額
   * @param tolerance 容忍誤差 (預設 1 元)
   * @returns boolean
   */
  static validateAmount(actualAmount: number, expectedAmount: number, tolerance: number = 1): boolean {
    return Math.abs(actualAmount - expectedAmount) <= tolerance;
  }

  /**
   * 格式化金額顯示
   * @param amount 金額
   * @param decimalPlaces 小數位數 (預設 0)
   * @returns 格式化的金額字串
   */
  static formatAmount(amount: number, decimalPlaces: number = 0): string {
    return amount.toLocaleString('zh-TW', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    });
  }

  // ============================================================
  // INFRA-01:定點整數運算基礎
  // 舊實現以固定 1e10 縮放,a > 900,719(Number.MAX_SAFE_INTEGER / 1e10)
  // 即超出 2^53,整數捨入保證失效;multiply 的中間值更高達 a*b*1e20,實質無效。
  // 改為依「實際小數位數」動態縮放:金額 2 位小數下,安全範圍可達 ~9 兆(9e13),
  // 遠超台股單一帳戶的可能規模。
  // ============================================================

  /** 取數字的小數位數(以字串表示判定,辨識 0.1 這類無法被二進位精確表示的值) */
  private static decimalPlaces(n: number): number {
    if (!Number.isFinite(n) || Number.isInteger(n)) return 0;
    const s = String(n);
    const dot = s.indexOf('.');
    if (dot === -1) return 0;
    // 科學記號時退回近似值(財務輸入不應出現)
    if (s.includes('e') || s.includes('E')) return 8;
    return Math.min(s.length - dot - 1, 8);
  }

  /**
   * 安全加法運算（整數定點,避免浮點誤差）
   * @param a 第一個數字
   * @param b 第二個數字
   * @returns 相加結果
   */
  static add(a: number, b: number): number {
    if (!Number.isFinite(a) || !Number.isFinite(b)) {
      return Number.isFinite(a) ? a : (Number.isFinite(b) ? b : 0);
    }
    const scale = Math.max(this.decimalPlaces(a), this.decimalPlaces(b), 2);
    const factor = Math.pow(10, scale);
    return (Math.round(a * factor) + Math.round(b * factor)) / factor;
  }

  /**
   * 安全減法運算（整數定點,避免浮點誤差）
   * @param a 第一個數字
   * @param b 第二個數字
   * @returns 相減結果
   */
  static subtract(a: number, b: number): number {
    if (!Number.isFinite(a) || !Number.isFinite(b)) {
      return Number.isFinite(a) ? a : 0;
    }
    const scale = Math.max(this.decimalPlaces(a), this.decimalPlaces(b), 2);
    const factor = Math.pow(10, scale);
    return (Math.round(a * factor) - Math.round(b * factor)) / factor;
  }

  /**
   * 安全乘法運算（整數定點,避免浮點誤差）
   * @param a 第一個數字
   * @param b 第二個數字
   * @returns 相乘結果
   */
  static multiply(a: number, b: number): number {
    if (!Number.isFinite(a) || !Number.isFinite(b)) {
      return 0;
    }
    const scaleA = this.decimalPlaces(a);
    const scaleB = this.decimalPlaces(b);
    const factorA = Math.pow(10, scaleA);
    const factorB = Math.pow(10, scaleB);
    return (Math.round(a * factorA) * Math.round(b * factorB)) / (factorA * factorB);
  }

  /**
   * 安全除法運算（以指定精度四捨五入）
   * @param a 被除數
   * @param b 除數
   * @param precision 小數位數 (預設 2)
   * @returns 相除結果，除數為0時返回0
   */
  static divide(a: number, b: number, precision: number = 2): number {
    if (!Number.isFinite(a) || !Number.isFinite(b) || b === 0) {
      return 0;
    }
    const factor = Math.pow(10, precision);
    return Math.round((a / b) * factor) / factor;
  }
}

// 便利函數導出
export const {
  calculateTradeAmount,
  calculateBrokerageFee,
  calculateTransactionTax,
  calculateCashDividend,
  calculateStockDividend,
  calculateBuyCost,
  calculateSellIncome,
  calculateDividendIncome,
  validateAmount,
  formatAmount
} = DecimalHelper;